import React from "react";
import { motion } from "framer-motion";
import { Building2, Check, Landmark, PiggyBank, Split, Wallet } from "lucide-react";
import { CardFrame, fadeUp } from "./IntakeCards";
import { CountUp } from "@/components/clientDemo/intro/IntroStats";

const P = "exchange-clearing-card";
const SPLITS = [["Rent", 30, "#D4AF37"], ["Case mgmt", 15, "#C9A227"], ["Savings", 25, "#10b981"], ["Loan", 10, "#0F766E"], ["Net cash", 20, "#F4D88A"]];

export function PaycheckSplitCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="paycheck" n={1} title="Paycheck Clearing & Auto-Splits" Icon={Split} active={active}>
      <div key={run}>
        <motion.div {...fadeUp(0.1)} className="flex items-center gap-2 text-xs">
          <span className="text-white">Marcus T. · Bridge Logistics</span>
          <span className="ml-auto font-mono text-base font-bold text-[#F4D88A]">$<CountUp to={1215} duration={1100} /></span>
          <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[9px]">CLEARED</span>
        </motion.div>
        <div className="mt-3 flex h-3 rounded-full overflow-hidden bg-white/5" data-testid="exchange-clearing-splitbar">
          {SPLITS.map(([k, pct, c], i) => (
            <motion.div key={k} style={{ background: c }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: 0.6 + i * 0.2, duration: 0.5 }} />
          ))}
        </div>
        <div className="mt-2 grid grid-cols-5 gap-1 text-[9px] text-slate-400">
          {SPLITS.map(([k, pct, c], i) => (
            <motion.div key={k} {...fadeUp(0.7 + i * 0.2)}><span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: c }} />{k}<div className="font-mono text-slate-200">${Math.round(12.15 * pct)}</div></motion.div>
          ))}
        </div>
      </div>
    </CardFrame>
  );
}

const PROPS = [["House 1", 4800], ["House 2", 5600], ["House 3", 4200]];

export function RentClearingCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="rent" n={2} title="Alliance Rent & Lease Clearinghouse" Icon={Building2} active={active}>
      <div key={run} className="space-y-1.5 text-xs">
        {PROPS.map(([h, amt], i) => (
          <motion.div key={h} {...fadeUp(0.1 + i * 0.2)} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center rounded-md bg-white/[0.03] border border-white/5 px-2.5 py-1.5">
            <span className="text-white">{h}</span>
            <span className="font-mono text-[#F4D88A]">${amt.toLocaleString()} recouped</span>
            <span className="text-emerald-300 text-[10px]">Landlord paid</span>
          </motion.div>
        ))}
        <motion.div {...fadeUp(1)} className="flex items-baseline gap-2 pt-1">
          <span className="text-slate-400">Unpaid balances</span>
          <motion.span className="font-mono font-bold text-white" initial={{ opacity: 1 }} animate={{ opacity: [1, 0.3, 1] }} transition={{ delay: 1, duration: 0.6 }}>$0</motion.span>
        </motion.div>
        <motion.div {...fadeUp(1.4)} data-testid="exchange-clearing-cleared" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 font-mono text-[10px] tracking-[0.12em] uppercase">
          <Check className="w-3 h-3" /> Campus obligations cleared
        </motion.div>
      </div>
    </CardFrame>
  );
}

function Countdown({ from, duration = 1600 }) {
  const [v, setV] = React.useState(from);
  React.useEffect(() => {
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - t0) / duration); setV(Math.round(from * (1 - p))); if (p < 1) raf = requestAnimationFrame(tick); };
    const start = setTimeout(() => { raf = requestAnimationFrame(tick); }, 500);
    return () => { clearTimeout(start); cancelAnimationFrame(raf); };
  }, [from, duration]);
  return <>${v.toLocaleString()}</>;
}

export function SavingsLoanCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="savings" n={3} title="Savings Bucket & Loan Payoff" Icon={PiggyBank} active={active}>
      <div key={run} className="text-xs">
        <div className="flex justify-between text-slate-400 mb-1"><span>Savings bucket · apartment deposit</span><span className="font-mono text-emerald-300">$2,500 / $2,500</span></div>
        <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-[#F4D88A]" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ delay: 0.2, duration: 1.4 }} />
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-slate-400">Loan balance</span>
          <span className="font-mono text-lg font-bold text-white tabular-nums" data-testid="exchange-clearing-loan"><Countdown from={640} /></span>
          <motion.span {...fadeUp(2.1)} className="text-emerald-300 font-mono text-[10px] uppercase">Paid off</motion.span>
        </div>
        <div className="mt-2 flex items-center gap-2 text-[11px]">
          <motion.span {...fadeUp(2.3)} className="px-2 py-1 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#F4D88A]">Fund release requested</motion.span>
          <motion.span {...fadeUp(2.6)} className="text-slate-500">→</motion.span>
          <motion.span {...fadeUp(2.9)} data-testid="exchange-clearing-released" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-400/40 text-emerald-300">
            <Wallet className="w-3 h-3" /> Staff disbursed
          </motion.span>
        </div>
      </div>
    </CardFrame>
  );
}

const WATERFALL = [["Projected CM revenue", 100], ["Realized gross CM cash", 92], ["Campus obligations cleared", 64], ["Net operating surplus", 28]];
const ACCOUNTS = ["Grant", "Operating", "Resident Escrow"];

export function WaterfallCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="waterfall" n={4} title="Revenue Waterfall & Accountability Ledger" Icon={Landmark} active={active}>
      <div key={run} className="space-y-1.5 text-[11px]">
        {WATERFALL.map(([k, w], i) => (
          <div key={k} className="grid grid-cols-[150px_1fr] items-center gap-2">
            <span className="text-slate-400 truncate">{k}</span>
            <div className="h-2.5 rounded bg-white/5 overflow-hidden">
              <motion.div className="h-full rounded bg-gradient-to-r from-[#C9A227] to-[#F4D88A]" initial={{ width: 0 }} animate={{ width: `${w}%` }} transition={{ delay: 0.2 + i * 0.3, duration: 0.6 }} />
            </div>
          </div>
        ))}
        <motion.div {...fadeUp(1.5)} className="flex flex-wrap items-center gap-1.5 pt-2">
          {ACCOUNTS.map((a) => <span key={a} className="px-2 py-0.5 rounded-full border border-white/10 text-slate-300 text-[10px]">{a}</span>)}
          <span data-testid="exchange-clearing-ledger" className="ml-auto inline-flex items-center gap-1 text-emerald-300 font-mono text-[10px] uppercase"><Check className="w-3 h-3" /> Every dollar on record</span>
        </motion.div>
      </div>
    </CardFrame>
  );
}
